import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';

import _, { noop } from 'lodash';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  ScrollArea,
  Slider,
  Stack,
  Text,
  TextInput,
} from '@cowbell/ui';

import { searchAgencyLocations } from './api/myAgent.api';
import { Modal } from '../components/modals/v2/helpers/v2.modal.helpers';

// helpers
import { fetchAccountById } from '../accounts/AccountActions';
import MyBrokerUnlinkIcon from '../_assets/svg/MyBrokerUnlink.svg';
import { useActor } from '../components/hooks/useActor';
import { getCowbellTranslations } from '../i18n/translations';
import { usePlatformLanguage } from '../utils/hooks/usePlatformLanguage';
import { rem } from './myAgent.styles';
import { AgencyPinPopover, type PinnedAgencyContact } from './AgencyPinPopover';
import {
  DISTANCE_UNIT_NAME,
  distanceToAgency,
  formatDistance,
  getDistanceUnit,
  RADIUS_RANGE,
} from './agencyDistance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { languages, type Languages } from '../i18n/i18n.language-config';
import { getIsoCountry } from '../i18n/country';

/**
 * Layout-only geometry. Everything visual — surfaces, type, spacing, colour —
 * comes from @cowbell/ui components; this covers the two-up grid and the result
 * row, which have no component equivalent.
 */
const styles = {
  resultRow: {
    display: 'flex',
    gap: 'var(--mantine-spacing-sm)',
    alignItems: 'flex-start',
    width: '100%',
    padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-md)',
    cursor: 'pointer',
    textAlign: 'left',
    background: 'none',
    // Order matters: a later `border: none` would reset the bottom edge, and
    // re-declaring only borderBottomStyle brings back the CSS defaults —
    // medium width in currentColor, i.e. a thick black rule.
    borderWidth: 0,
    borderBottomWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--mantine-color-default-border)',
  } as const,
  resultIndex: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: rem(22),
    height: rem(22),
    borderRadius: '999px',
    border: '1px solid var(--mantine-color-blue-6)',
    color: 'var(--mantine-color-blue-6)',
    fontSize: rem(11),
    fontWeight: 600,
  } as const,
  resultIndexCurrent: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: rem(22),
    height: rem(22),
    borderRadius: '999px',
    border: '1px solid var(--mantine-color-dark-9)',
    background: 'var(--mantine-color-dark-9)',
    color: 'var(--mantine-color-white)',
    fontSize: rem(10),
  } as const,
  mapFill: { height: '100%', width: '100%' } as const,
};

/**
 * How many of the nearest results the map frames. Every pin still renders; this
 * only stops one distant outlier from zooming the map out to the world.
 */
const BOUNDS_FOCUS_COUNT = 10;

/**
 * One fixed height for the whole search band, in rem so it scales with the root
 * font. Both columns take it and the result list scrolls inside its share, so
 * the band never grows or shrinks with the number of results.
 */
const BAND_HEIGHT = rem(440);

/**
 * Where the map opens when nothing else supplies a centre. Typed against every
 * platform language so a new region is a compile error here rather than a blank
 * lookup at render time.
 */
const MAP_CENTER: Readonly<
  Record<Languages, { readonly latitude: number; readonly longitude: number }>
> = {
  [languages['en-US']]: {
    latitude: 37.658673,
    longitude: -121.90203,
  },
  [languages['en-GB']]: {
    latitude: 51.50887,
    longitude: -0.14418,
  },
  [languages['en-AU']]: {
    latitude: -33.8688,
    longitude: 151.2093,
  },
};

interface FindMyAgentProps {
  readonly myAgent?: any;
  /** Agent contact keyed by agencyId — the directory search returns none. */
  readonly contactsByAgencyId?: Readonly<Record<string, PinnedAgencyContact>>;
  /**
   * Area to search by default — the hard-linked agency's location, so the page
   * opens on agencies around the policyholder's own agent rather than nowhere.
   */
  readonly defaultArea?: string;
  /** Where to centre the map before any search: the linked agency's location. */
  readonly defaultCenter?: { readonly lat: number; readonly lon: number };
  /** The policyholder's own agency, pinned to the top of the results. */
  readonly currentAgencyId?: string;
  /**
   * The linked agency's own location record. Unioned into the results so the
   * policyholder's agency is always listed, even when the search term does not
   * match it.
   */
  readonly currentAgencyLocation?: {
    readonly id?: string;
    readonly agencyId?: string;
    readonly name?: string;
    readonly address1?: string;
    readonly address2?: string;
    readonly city?: string;
    readonly state?: string;
    readonly zipCode?: string;
    readonly phoneNumber?: string;
    readonly location?: { readonly lat: number; readonly lon: number };
  };
  readonly utils?: any;
  readonly data?: any;
}

function FindMyAgent(props: Readonly<FindMyAgentProps>) {
  const actor = useActor();
  const dispatch = useDispatch();
  const orgData = useSelector(
    (state: any) => state.account.policyHolderAccount
  );
  const platformRegion = usePlatformLanguage();
  const geocodeCountry = getIsoCountry(platformRegion);
  // Australia is metric. The unit runs through the radius, the directory's geo
  // filter and every row label alike, so nothing converts between the two.
  const distanceUnit = getDistanceUnit(platformRegion);
  const radiusRange = RADIUS_RANGE[distanceUnit];

  // One field, not two: the directory's free-text `search` matches agency name,
  // city, state and ZIP alike, so a user typing an agency name into an "area"
  // box no longer silently returns nothing.
  const [term, setTerm] = useState('');
  const [mapUnavailable, setMapUnavailable] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedAgency, setSelectedAgencyState] = useState<any>(null);
  // The distance filter is opt-in, and off by default: agency records carry
  // coordinates that disagree with their own addresses (a CA 93650 row geocoded
  // to Ireland, another to Florida, and the linked agency itself ~250mi from the
  // other agencies sharing its ZIP). Filtering by default would silently drop
  // valid matches, so results are sorted nearest-first with the distance shown
  // and the user turns the limit on when they want it.
  // Whether the last search could anchor itself anywhere. Geocoding a term can
  // fail (75368 is a PO-box ZIP Google returns nothing for) and an account may
  // carry no coordinates, and without an origin there is no distance to limit by.
  // Whether the distance limit can apply to what is currently listed. It cannot
  // when there is nothing to measure from, and it must not on a typed query —
  // geocoding a name or a state yields a centroid, and a radius around it would
  // hide the matches the user asked for.
  const [limitApplies, setLimitApplies] = useState(true);
  const [limitByDistance, setLimitByDistance] = useState(true);
  const [radius, setRadius] = useState<number>(radiusRange.initial);
  const radiusRef = useRef<number>(radiusRange.initial);

  // Instance-like refs mirror the class's mutable fields so the imperative
  // Google Maps handlers always read the latest values (no stale closures).
  const termRef = useRef('');
  // Whether the current search is the seeded "near me" one rather than something
  // the user typed. This used to be inferred by comparing the term against
  // `defaultArea`, which breaks the moment that prop moves: the field is seeded
  // from the account ZIP, the linked agency then resolves with a different one,
  // and the search silently stopped counting as near-me - no radius, no limit.
  const nearMeRef = useRef(true);
  // Set once the user edits the field, so a late-arriving default never
  // overwrites what they are typing.
  const userEditedRef = useRef(false);
  const mapsRef = useRef<any>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any>({});
  const elRefsRef = useRef<any>({});
  const listenersRef = useRef<any[]>([]);
  const blueDotRef = useRef<any>(null);
  const selectedAgencyRef = useRef<any>(null);
  const loadedAccountRef = useRef(false);
  const intervalIdRef = useRef<any>(null);
  const actorRef = useRef(actor);
  actorRef.current = actor;

  const setSelectedAgency = (value: any) => {
    selectedAgencyRef.current = value;
    setSelectedAgencyState(value);
  };

  const scrollToElem = (option: any) => {
    elRefsRef.current[option.id]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const clearSelectedAgency = () => {
    const current = selectedAgencyRef.current;
    if (current && markersRef.current[current.id]) {
      markersRef.current[current.id].setIcon(null);
      markersRef.current[current.id].setAnimation(null);
    }
    setSelectedAgency(null);
  };

  const handleAgencyClick = (option: any) => () => {
    // Not every row has a marker: the policyholder's own agency is listed even
    // when it has no coordinates to place it at.
    const previous = markersRef.current[selectedAgencyRef.current?.id];
    if (previous) {
      previous.setIcon(null);
      previous.setAnimation(null);
    }
    const marker = markersRef.current[option.id];
    if (marker) {
      marker.setIcon(blueDotRef.current);
      marker.setAnimation(mapsRef.current.Animation.BOUNCE);
    }
    setSelectedAgency(option);
  };

  const markerClickHandler = (option: any) => () => {
    scrollToElem(option);
    handleAgencyClick(option)();
  };

  /**
   * Places an agency that has an address but no coordinates.
   *
   * The hard-link endpoint returns AgencyDto, which carries address1/city/state/
   * zipCode but no geo point, so the policyholder's own agency would list without
   * ever appearing on the map. The Maps API is already loaded for the markers, so
   * its geocoder answers this without another service.
   */
  const withCoordinates = (option: any): Promise<any> => {
    if (typeof option?.location?.lat === 'number') {
      return Promise.resolve(option);
    }
    const address = [
      option?.address1,
      option?.city,
      option?.state,
      option?.zipCode,
    ]
      .filter(Boolean)
      .join(', ');
    if (!address || !mapsRef.current?.Geocoder) {
      return Promise.resolve(option);
    }
    return new Promise((resolve) => {
      new mapsRef.current.Geocoder().geocode(
        { address, componentRestrictions: { country: geocodeCountry } },
        (results: any, status: any) => {
          const point = results?.[0]?.geometry?.location;
          if (status === 'OK' && point) {
            resolve({
              ...option,
              location: { lat: point.lat(), lon: point.lng() },
            });
            return;
          }
          // A failed geocode is ordinary — the row still lists, just unpinned.
          resolve(option);
        }
      );
    });
  };

  /**
   * Where distances are measured from: the place the user searched.
   *
   * Measuring from the policyholder's own origin made the radius meaningless for
   * any other place — searching a city 500 miles away returned nothing, because
   * every result sat outside a bubble drawn somewhere else. The geocoder is
   * already loaded for the markers.
   */
  const originForTerm = (term: string, nearMe: boolean): Promise<any> => {
    if (nearMe && props.defaultCenter) {
      return Promise.resolve(props.defaultCenter);
    }
    // A hard-linked agency reaches us as an AgencyDto, which carries an address
    // but no geo point, so the seeded search had no origin at all: no distances,
    // a distance limit that could never apply (the checkbox sat disabled) and a
    // map framed to every result in the country. Geocode the seeded area to
    // recover the origin its coordinates would have given us.
    //
    // Only a "near me" search measures from anywhere else: geocoding an arbitrary
    // query treats an agency name or a state as an address and returns a
    // centroid, and a radius drawn around that centroid hides the very matches
    // the user asked for. Its origin is still resolved — it just goes unused by
    // the radius filter, which never applies to a typed query.
    const address = nearMe ? props.defaultArea : term;
    if (!address) {
      return Promise.resolve(props.defaultCenter);
    }
    if (originCacheRef.current.term === address) {
      return Promise.resolve(originCacheRef.current.origin);
    }
    if (!mapsRef.current?.Geocoder) {
      return Promise.resolve(props.defaultCenter);
    }
    return new Promise((resolve) => {
      new mapsRef.current.Geocoder().geocode(
        { address, componentRestrictions: { country: geocodeCountry } },
        (results: any, status: any) => {
          const point = results?.[0]?.geometry?.location;
          const origin =
            status === 'OK' && point
              ? { lat: point.lat(), lon: point.lng() }
              : props.defaultCenter;
          originCacheRef.current = { term: address, origin };
          resolve(origin);
        }
      );
    });
  };

  /**
   * The distance limit, handed to the directory rather than applied to whatever
   * page it returned. Only a "near me" search is limited: a typed query is an
   * explicit request, and geocoding a name or a state yields a centroid whose
   * radius would hide the very matches the user asked for.
   */
  const geoParamsFor = (origin: any, nearMe: boolean) => {
    if (
      !origin ||
      radiusRef.current === 0 ||
      !nearMe ||
      typeof origin.lat !== 'number' ||
      typeof origin.lon !== 'number'
    ) {
      return {};
    }
    return {
      lat: origin.lat,
      lon: origin.lon,
      dist: `${radiusRef.current}${distanceUnit}`,
    };
  };

  /** Drops a pin for one result and wires its click through to the popover. */
  const addMarker = (option: any) => {
    const marker = new mapsRef.current.Marker({
      position: { lng: option.location.lon, lat: option.location.lat },
      map: mapRef.current,
    });
    markersRef.current[option.id] = marker;
    listenersRef.current.push(
      mapsRef.current.event.addListener(
        marker,
        'click',
        markerClickHandler(option)
      )
    );
  };

  const agentsSearch = () => {
    const run = searchRunRef.current + 1;
    searchRunRef.current = run;
    setIsSearching(true);
    blueDotRef.current = {
      url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
      scaledSize: new mapsRef.current.Size(50, 50),
    };

    const searchTerm = termRef.current || '*';
    // Read once, at the start of the run. A geocode makes a run's tail
    // arbitrarily long, and reading the live ref further down let a keystroke
    // land mid-flight and turn a seeded radius search into a ZIP text match -
    // silently, since typing does not itself start a new search.
    const nearMe = nearMeRef.current;
    // The origin is resolved before the request, not after, so the radius can go
    // to the directory as a geo filter. Filtering a page client-side only ever
    // narrowed the 25 rows the backend happened to return, which left a genuinely
    // nearby agency in the next ZIP out of a "within 50 miles" search entirely.
    originForTerm(searchTerm, nearMe)
      .then((origin) => {
        const geo: any = geoParamsFor(origin, nearMe);
        return searchAgencyLocations({
          // A radius already says "everything within N miles of here". Sending
          // the seeded ZIP as well makes the directory text-match it too, and an
          // agency one ZIP over - exactly what the radius is for - drops out.
          search: geo.dist ? '*' : searchTerm,
          page: 0,
          // `size` is the key the mapping emits; `pageSize` is dropped silently.
          size: 25,
          ...geo,
        }).then((resp) => ({ resp, origin }));
      })
      .then(({ resp, origin }) => {
        const bounds = new mapsRef.current.LatLngBounds();
        // Not every agency location carries a geo point (virtual offices, records
        // that never geocoded). Reading option.location.lon on those threw inside
        // this handler, which killed the whole search — no markers *and* no result
        // list. Map only what can be placed; the list below still shows them all.
        const found = resp?.data?.content ?? [];
        // The policyholder's own agency is always listed, whether or not the
        // search term happened to match it.
        const currentAgency = currentAgencyLocationRef.current;
        const alreadyFound =
          !currentAgency?.agencyId ||
          found.some((row: any) => row.agencyId === currentAgency.agencyId);
        const withCurrent = alreadyFound ? found : [currentAgency, ...found];
        return Promise.all(withCurrent.map(withCoordinates)).then(
          (placed: any[]) => {
            if (run !== searchRunRef.current) {
              return;
            }
            setLimitApplies(!!origin && nearMe);
            const nextOptions = placed
              .filter((option: any) => {
                // The policyholder's own agency is listed even without coordinates —
                // it just gets no pin. Everything else needs a point to be mappable.
                if (
                  currentAgencyIdRef.current &&
                  option?.agencyId === currentAgencyIdRef.current
                ) {
                  return true;
                }
                return (
                  typeof option?.location?.lat === 'number' &&
                  typeof option?.location?.lon === 'number'
                );
              })
              .map((option: any) => ({
                ...option,
                distance: distanceToAgency(
                  origin,
                  option.location,
                  distanceUnit
                ),
              }))
              // Distances are measured from the searched place, so the radius means
              // "near what I asked for". It also keeps the map honest where a record's
              // address and coordinates disagree — this data has a CA 93650 row
              // geocoded to Ireland, another to Florida.
              .filter((option: any) => {
                // The policyholder's own agency is always listed, however far away.
                if (
                  currentAgencyIdRef.current &&
                  option?.agencyId === currentAgencyIdRef.current
                ) {
                  return true;
                }
                // A typed query is an explicit request; never drop its matches.
                if (!nearMe) {
                  return true;
                }
                if (!origin || radiusRef.current === 0) {
                  return true;
                }
                if (typeof option.distance?.value !== 'number') {
                  return true;
                }
                return option.distance.value <= radiusRef.current;
              })
              .sort((a: any, z: any) => {
                // The policyholder's own agency leads, however far away it is.
                const currentId = currentAgencyIdRef.current;
                if (currentId) {
                  if (a.agencyId === currentId) {
                    return -1;
                  }
                  if (z.agencyId === currentId) {
                    return 1;
                  }
                }
                return (
                  (a.distance?.value ?? Number.MAX_VALUE) -
                  (z.distance?.value ?? Number.MAX_VALUE)
                );
              });
            Object.values(markersRef.current).forEach((marker) => {
              // @ts-expect-error TS(2571): Object is of type 'unknown'.
              marker.setMap(null);
            });
            markersRef.current = {};

            const mappable = nextOptions.filter(
              (option: any) => typeof option?.location?.lat === 'number'
            );

            mappable.forEach(addMarker);

            if (mappable.length > 1) {
              // Frame the nearest results rather than every pin. Results are already
              // sorted nearest-first, and a single far outlier — this data has agencies
              // claiming a California ZIP while geocoded to Ireland or Australia —
              // would otherwise zoom the map out to the whole world.
              const framed = [...mappable]
                .sort(
                  (a: any, z: any) =>
                    (a.distance?.value ?? Number.MAX_VALUE) -
                    (z.distance?.value ?? Number.MAX_VALUE)
                )
                .slice(0, BOUNDS_FOCUS_COUNT);
              framed.forEach((option: any) => {
                bounds.extend(
                  new mapsRef.current.LatLng(
                    option.location.lat,
                    option.location.lon
                  )
                );
              });
              mapRef.current.fitBounds(bounds);
              mapRef.current.panToBounds(bounds);
            } else if (mappable.length === 1) {
              const marker = markersRef.current[mappable[0].id];
              mapRef.current.setCenter(
                new mapsRef.current.LatLng(
                  marker.position.lat(),
                  marker.position.lng()
                )
              );
            }
            setOptions(nextOptions);
            setSearched(true);
            setIsSearching(false);
          }
        );
      });
  };

  // Keep a stable debounced search that always invokes the latest closure.
  // Seeding only the displayed value left locationRef at '*', so the default
  // search queried everything; and nothing ran a search on mount at all.
  // Each search is stamped; geocoding makes a run's tail arbitrarily long, so a
  // slower earlier run could otherwise land after a later one and replace it.
  const searchRunRef = useRef(0);
  const originCacheRef = useRef<{ term?: string; origin?: any }>({});
  const seededRef = useRef<string | undefined>(undefined);
  const didDefaultSearchRef = useRef(false);

  // Stable identity: google-map-react re-invokes onGoogleApiLoaded whenever the
  // handler changes, so an inline arrow re-fired the default search on every
  // render — racing whatever the user was typing.
  const handleApiLoaded = useCallback(({ map, maps }: any) => {
    mapRef.current = map;
    mapsRef.current = maps;
    // The search builds marker icons and bounds, so it needs the Maps API; the
    // default run waits for it rather than firing on mount.
    if (!didDefaultSearchRef.current) {
      didDefaultSearchRef.current = true;
      agentsSearchRef.current();
    }
  }, []);

  // The default search fires as soon as the Maps API is ready, which is often
  // before useResolvedAgent has answered — so the linked agency has to be read
  // through refs rather than captured from that render's props.
  const currentAgencyIdRef = useRef(props.currentAgencyId);
  currentAgencyIdRef.current = props.currentAgencyId;
  const currentAgencyLocationRef = useRef(props.currentAgencyLocation);
  currentAgencyLocationRef.current = props.currentAgencyLocation;

  const agentsSearchRef = useRef<() => void>(agentsSearch);
  agentsSearchRef.current = agentsSearch;
  const searchRef = useRef<any>(null);
  if (!searchRef.current) {
    searchRef.current = _.debounce(() => agentsSearchRef.current(), 500);
  }

  const handleSearch = (event: any) => {
    event.preventDefault();
    searchRef.current();
  };

  const handleChange = (event: any) => {
    const { value } = event.target;
    setTerm(value);
    termRef.current = value;
    userEditedRef.current = true;
    nearMeRef.current = false;
  };

  useEffect(() => {
    intervalIdRef.current = setInterval(() => {
      const currentActor = actorRef.current;
      if (
        Object.keys(currentActor.account).length > 0 &&
        !loadedAccountRef.current
      ) {
        dispatch(fetchAccountById(currentActor.account.id) as any);
        loadedAccountRef.current = true;
        window.clearInterval(intervalIdRef.current);
      }
    }, 100);

    return () => {
      listenersRef.current.forEach((listener: any) => {
        mapsRef.current.event.removeListener(listener);
      });
      window.clearInterval(intervalIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A search that ran before the linked agency resolved would not include it,
  // and would have been anchored on the wrong place. The opening search already
  // covers whatever these were on mount, so only a later change re-runs it.
  const seenDefaultsRef = useRef<string | null>(null);
  const defaultLat = props.defaultCenter?.lat;
  const defaultLon = props.defaultCenter?.lon;
  useEffect(() => {
    // Coordinates are part of the default, not just the area string: the
    // directory can resolve a geo point, or the browser answer its permission
    // prompt, long after the area has settled. Without them the search stayed
    // anchored on the coarser origin it started with.
    const defaults = [
      props.currentAgencyId ?? '',
      props.defaultArea ?? '',
      defaultLat ?? '',
      defaultLon ?? '',
    ].join('|');
    const isFirstRun = seenDefaultsRef.current === null;
    const changed = seenDefaultsRef.current !== defaults;
    seenDefaultsRef.current = defaults;

    if (isFirstRun || !changed || userEditedRef.current) {
      return;
    }
    if (didDefaultSearchRef.current) {
      searchRef.current();
    }
  }, [props.currentAgencyId, props.defaultArea, defaultLat, defaultLon]);

  // The unit can resolve after the first render: on an environment turned on by
  // feature flag the region arrives with GrowthBook rather than from the
  // hostname, so the slider would otherwise keep its imperial default and
  // relabel it "50 km" - off this slider's step grid as well as its scale. The
  // next search picks the corrected radius up; this does not fire one itself.
  const seenUnitRef = useRef(distanceUnit);
  useEffect(() => {
    if (seenUnitRef.current === distanceUnit) {
      return;
    }
    seenUnitRef.current = distanceUnit;
    setRadius(radiusRange.initial);
    radiusRef.current = limitByDistance ? radiusRange.initial : 0;
  }, [distanceUnit, radiusRange.initial, limitByDistance]);

  /**
   * Google calls this global when it refuses the key — most often because the
   * host is not on the key's referrer allowlist, which is how every non-US
   * region lost its map. Nothing else reports it: the SDK simply never draws,
   * leaving a blank card that can only be diagnosed from the browser console.
   *
   * The map stays mounted, only hidden: the search waits on `onGoogleApiLoaded`
   * and dereferences the Maps API unguarded, so unmounting it here would trade a
   * dead map for a dead search — the one thing still working on these hosts.
   */
  useEffect(() => {
    const previous = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => setMapUnavailable(true);
    return () => {
      (window as any).gm_authFailure = previous;
    };
  }, []);

  const { t } = getCowbellTranslations();

  let address = props.defaultArea ?? '';
  let agentName = '';
  if (props.myAgent) {
    const {
      address1,
      city,
      zipCode,
      state: agentState,
      agentFirstName,
      agentLastName,
      contactFirstName,
      contactLastName,
    } = props.myAgent;

    address = [address1, city, agentState, zipCode].filter(Boolean).join(', ');
    if (agentFirstName) {
      agentName = [agentFirstName, agentLastName].filter(Boolean).join(', ');
    } else {
      agentName = [contactFirstName, contactLastName]
        .filter(Boolean)
        .join(', ');
    }
  }

  // `address` covers both the seeded default area and the legacy myAgent prop.
  // It has to reach termRef, not just the input, or the query still sends '*'.
  //
  // Re-seeded whenever the default changes, not just the first time: the page
  // often opens on the account ZIP and only later learns the linked agency's,
  // and a one-shot seed left the field showing the wrong one.
  if (address && !userEditedRef.current && seededRef.current !== address) {
    seededRef.current = address;
    termRef.current = address;
    nearMeRef.current = true;
    if (term !== address) {
      setTerm(address);
    }
  }

  let agenciesList = null;
  if (options.length > 0) {
    const list = options.map((option: any, index: number) => {
      elRefsRef.current[option.id] = React.createRef();

      // eslint-disable-next-line no-shadow
      const { name, city, state, zipCode } = option;

      const data = {
        ...option,
        linking: true,
        getAgent: true,
        utils: props.utils,
      };

      const isSelected = Boolean(
        selectedAgency && selectedAgency.id === option.id
      );

      const isCurrentAgency =
        !!props.currentAgencyId && option.agencyId === props.currentAgencyId;

      // In the unit this row was measured in, which is not necessarily the one
      // the panel is now offering: a region resolving late re-bases the slider
      // but cannot re-measure a list that has already been searched.
      const distanceLabel = formatDistance(option.distance);

      return (
        <li key={option.id} ref={elRefsRef.current[option.id]}>
          <Box
            component="button"
            type="button"
            onClick={handleAgencyClick(option)}
            aria-current={isSelected}
            style={styles.resultRow}
            bg={isSelected ? 'var(--mantine-color-blue-light)' : undefined}
          >
            <span
              style={
                isCurrentAgency ? styles.resultIndexCurrent : styles.resultIndex
              }
            >
              {isCurrentAgency ? <FontAwesomeIcon icon={faStar} /> : index + 1}
            </span>
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={600} truncate>
                {name}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                {[city, state].filter(Boolean).join(', ')} {zipCode}
              </Text>
              {isCurrentAgency && (
                <Text size="xs" fw={600} mt={2}>
                  {`Your current ${t('agent').toLowerCase()}`}
                </Text>
              )}
            </Stack>
            {distanceLabel && (
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {distanceLabel}
              </Text>
            )}
          </Box>
        </li>
      );
    });
    agenciesList = (
      <Stack gap={0} style={{ flex: 1, minHeight: 0 }}>
        <Group
          justify="space-between"
          px="md"
          py="xs"
          style={{
            borderBottom: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">
            {`${options.length} ${
              options.length === 1 ? t('agency') : t('agencies')
            }`}
          </Text>
          <Text size="xs" c="dimmed">
            Click a result to locate it
          </Text>
        </Group>
        <ScrollArea style={{ flex: 1, minHeight: 0 }}>
          <Box
            component="ul"
            style={{ margin: 0, padding: 0, listStyle: 'none' }}
          >
            {list}
          </Box>
        </ScrollArea>
      </Stack>
    );
  } else if (searched) {
    agenciesList = (
      <Stack align="center" p="xl" gap={4}>
        <Text size="sm" fw={600}>
          {`No ${t('agencies')?.toLowerCase()} found`}
        </Text>
        <Text size="xs" c="dimmed">
          Try a different name, city or ZIP.
        </Text>
      </Stack>
    );
  }

  // Resolved before use, not inline: as a `_.get` default argument this was
  // evaluated eagerly, so a region absent from the table threw on every render
  // that had no centre of its own — taking the whole page down rather than
  // losing the centre. The fallback covers a region the type cannot, such as one
  // reaching a stale bundle.
  const regionCenter =
    MAP_CENTER[platformRegion] ?? MAP_CENTER[languages['en-US']];
  // No early return on a missing policyholder account: orgData only supplies the
  // map's default centre, which already falls back to the platform region. Bailing
  // out here blanked the whole search — and the search must be available in every
  // state (FIR-21113).
  const defaultMapProps = {
    center: {
      lat:
        props.defaultCenter?.lat ??
        _.get(orgData, 'location.lat', regionCenter.latitude),
      lng:
        props.defaultCenter?.lon ??
        _.get(orgData, 'location.lon', regionCenter.longitude),
    },
    zoom: 12,
  };

  return (
    <Grid gutter="md" align="stretch">
      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Card
          withBorder
          radius="md"
          padding={0}
          h={BAND_HEIGHT}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Box
            component="form"
            onSubmit={handleSearch}
            noValidate
            p="md"
            style={{
              borderBottom: '1px solid var(--mantine-color-default-border)',
            }}
          >
            <Text fw={600} mb="sm">
              {`Find a Cowbell Cyber Insurance ${t('agent')}`}
            </Text>
            <TextInput
              id="agency-search"
              label={`Search ${t('agencies')}`}
              placeholder={`${t('agency')} name, city, state or ZIP`}
              value={term}
              onChange={handleChange}
            />
            <Stack gap="xs" mt="md">
              <Checkbox
                size="xs"
                label={
                  limitApplies
                    ? `Limit to ${radius} ${distanceUnit}`
                    : 'Distance limit applies to searches near you'
                }
                disabled={!limitApplies}
                checked={limitByDistance && limitApplies}
                onChange={(event) => {
                  const on = event.currentTarget.checked;
                  setLimitByDistance(on);
                  radiusRef.current = on ? radius : 0;
                  searchRef.current();
                }}
              />
              <Slider
                aria-label={`Search radius in ${DISTANCE_UNIT_NAME[distanceUnit]}`}
                min={radiusRange.min}
                max={radiusRange.max}
                step={radiusRange.step}
                value={radius}
                disabled={!limitByDistance || !limitApplies}
                label={(value: number) => `${value} ${distanceUnit}`}
                onChange={setRadius}
                // Re-search on release, not on every pixel of the drag.
                onChangeEnd={(next: number) => {
                  radiusRef.current = limitByDistance ? next : 0;
                  if (limitByDistance) {
                    searchRef.current();
                  }
                }}
              />
            </Stack>
            <Button type="submit" loading={isSearching} fullWidth mt="md">
              {`Find My ${t('agent')}`}
            </Button>
          </Box>
          {agenciesList}
        </Card>
      </Grid.Col>
      <Grid.Col span={{ base: 12, lg: 8 }}>
        <Card withBorder radius="md" padding={0} h={BAND_HEIGHT}>
          {mapUnavailable && (
            <Stack align="center" justify="center" gap="xs" h="100%" p="md">
              <Text fw={600}>Map unavailable</Text>
              <Text size="sm" c="dimmed" ta="center">
                {`The map could not be loaded for this site. Searching for ${t(
                  'agencies'
                )?.toLowerCase()} still works.`}
              </Text>
            </Stack>
          )}
          <Box style={styles.mapFill} hidden={mapUnavailable}>
            <GoogleMapReact
              bootstrapURLKeys={{
                key: 'AIzaSyAxPqPUCOsnWTbXGchFvIJl-Lyh-o0Ok2k',
                language: platformRegion,
              }}
              defaultCenter={defaultMapProps.center}
              defaultZoom={defaultMapProps.zoom}
              onGoogleApiLoaded={handleApiLoaded}
            >
              {selectedAgency?.location && (
                <AgencyPinPopover
                  lat={selectedAgency.location.lat}
                  lng={selectedAgency.location.lon}
                  agency={selectedAgency}
                  contact={
                    props.contactsByAgencyId?.[
                      selectedAgency.agencyId as string
                    ]
                  }
                  onClose={clearSelectedAgency}
                />
              )}
            </GoogleMapReact>
          </Box>
        </Card>
      </Grid.Col>
    </Grid>
  );
}
export default FindMyAgent;
